/**
 * ビューワ本体。オプション解決・資源の獲得と解放・状態の所有・各層の仲介を行う。
 *
 * 現在ページ／ズームの真実は常にここにあり、エンジンは表示を担うだけにする。
 * 生成（create）で獲得したものは、すべて dispose() で解放する（M2 verify）。
 */
import { GLOBAL_NAME } from '../brand';
import { pagesOf, spreadAt, spreadOf } from '../engine/layout';
import { createFallbackEngine, selectEngine } from '../engine/select';
import { createTranslator } from '../i18n/i18n';
import type { Translate } from '../i18n/i18n';
import { canonicalViewUrl } from '../link/canonical-url';
import type { HashState } from '../link/hash-codec';
import { HashRouter } from '../link/hash-router';
import { buildShareUrl } from '../link/share-url';
import { OverlayLayer } from '../overlay/overlay-layer';
import { fetchOverlays } from '../overlay/overlay-store';
import { OverlayRegistry } from '../overlay/registry';
import { linkRenderer } from '../overlay/renderers/link';
import { audioRenderer, videoRenderer } from '../overlay/renderers/media';
import { mediaNoteRenderer, mediaPopupRenderer } from '../overlay/renderers/media-note';
import { formPopupRenderer, formRenderer } from '../overlay/renderers/form';
import { byDisplay } from '../overlay/renderers/popup';
import { HighlightLayer } from '../search/highlight-layer';
import { searchInBook } from '../search/search-client';
import { resolveDelivery } from '../source/delivery';
import { LockedPagesProvider, isPageAllowed, lockedNotice } from '../source/locked-pages';
import { createSource } from '../source/select';
import { createTextSource } from '../source/text-layer';
import type { TextSegment, TextSource } from '../source/text-layer';
import { createThumbSource } from '../source/thumbs';
import type { ThumbSource } from '../source/thumbs';
import { FlipSound } from '../sound/flip-sound';
import { MARK_TYPES, nowSec, sameItem } from '../user-data/store';
import type { MarkType, UserItem } from '../user-data/store';
import { Comments, type LikeState } from '../user-data/comments';
import { Session } from '../user-data/session';
import { UserData, apiBaseFrom } from '../user-data/user-data';
import { acquireStyles } from '../theme/styles';
import { applyTheme } from '../theme/theme';
import type { ViewerApi } from '../ui/api';
import { Shell } from '../ui/shell';
import type { ControlDef } from '../ui/toolbar';
import { UnlockForm, isPasswordRequired, unlockUrlFrom } from '../ui/unlock-form';
import { Analytics } from '../events/analytics';
import { DisposeRegistry } from './dispose-registry';
import type { ResourceCounts } from './dispose-registry';
import type { EngineContext, RenderEngine } from './engine';
import { TypedEmitter } from './events';
import type {
  EngineFailureReason,
  InternalBus,
  InternalEventMap,
  PublicEventMap,
  PublicEventName,
} from './events';
import { ViewerError, isAbortError } from './errors';
import { ManifestSession } from './manifest';
import type { BookManifest, OutlineNode } from './manifest';
import { resolveOptions } from './options';
import type { EngineKind, ReadingDirection, ViewerOptions, ViewerOptionsInput } from './options';
import type { PageRaster, SourceProvider } from './source';

export type ViewerStatus = 'created' | 'loading' | 'ready' | 'error' | 'disposed';

/** ズーム・パン後に URL を書き戻すまでの待ち（ms）。検索の入力待ち 250ms と同オーダー。 */
const HASH_WRITE_MS = 300;

/** 再開位置を保存するまでの待ち（ms）。連続めくりで毎回書かないための間引き。 */
const PROGRESS_SAVE_MS = 1000;

export interface ViewerState {
  page: number;
  label: string;
  pageCount: number;
  zoom: number;
  spread: boolean;
  direction: ReadingDirection;
  fullscreen: boolean;
  lightbox: boolean;
  /** 実際に描画しているエンジン（3D が使えず降格した場合は 'css2d'）。 */
  engine: Exclude<EngineKind, 'auto'>;
  /** めくり音が有効か。 */
  sound: boolean;
  status: ViewerStatus;
  /** 現在のページ・ズーム位置を再現する絶対 URL（FR-API-02・FR-LINK-05）。 */
  shareUrl: string;
  /**
   * 目印の状態（EP-1）。bookmark/sticky は**現在ページ**に、shelf は**このブック**に
   * 付いているか。ユーザーデータ無効時はすべて false。
   */
  marks: Record<MarkType, boolean>;
  /** このブックのいいね件数（EP-1 公開データ・未取得は 0）。 */
  likes: number;
  /** 現在の閲覧者がいいね済みか。 */
  liked: boolean;
  /** 会員としてログイン中か（投稿面・いいねの出し分け）。 */
  signedIn: boolean;
  /** 原本 PDF を渡せるか（`ui.download`・PDF 配信モード・PDF の権限フラグの AND・FR-DL-01/02）。 */
  canDownload: boolean;
  /** 原本 PDF を印刷用に開けるか（`ui.print`・PDF 配信モード・PDF の権限フラグの AND・FR-PRINT-01）。 */
  canPrint: boolean;
}

export class Viewer implements ViewerApi {
  private readonly options: ViewerOptions;
  private readonly registry = new DisposeRegistry();
  private readonly bus: InternalBus;
  private readonly translate: Translate;
  private readonly shell: Shell;
  private readonly releaseStyles: () => void;
  private readonly releaseTheme: () => void;
  private readonly hash: HashRouter | null;
  private readonly sound: FlipSound | null;
  private readonly highlights: HighlightLayer;
  /** オーバーレイの描き手（EP-2 の公開拡張点 — `app.overlays.registerRenderer`）。 */
  readonly overlays = new OverlayRegistry();
  /** 誌面オーバーレイ層（`overlays: false` では作らない）。 */
  private overlayLayer: OverlayLayer | null = null;
  /** 進行中の検索要求（新しい検索で打ち切る）。 */
  private searchAbort: AbortController | null = null;
  /** 閲覧イベントの収集（EP-6）。バスを購読するだけで、機能側はこれを知らない。 */
  private readonly analytics: Analytics;

  private session: ManifestSession | null = null;
  private provider: SourceProvider | null = null;
  /** サーバ生成サムネイル（無いブックでは null → provider の描画へ落ちる）。 */
  private thumbs: ThumbSource | null = null;
  /** 誌面の代替テキスト源（テキスト層の無いブックでは null → 見出し行だけに縮退）。 */
  private textSource: TextSource | null = null;
  /** 代替テキストの取得（ページが変わったら前の要求は捨てる）。 */
  private pageTextAbort: AbortController | null = null;
  /** 代替テキストを出しているページの並び（同じなら取り直さない）。 */
  private pageTextKey = '';
  private engine: RenderEngine | null = null;
  /** 実際に使っている配信モード（FR-DL-01・FR-PRINT-01 は PDF 配信モードだけの機能）。 */
  private delivery: 'pdf' | 'image' | null = null;
  /** 原本 PDF 自身が許している操作（FR-DL-02）。取得前は両方 false に倒す。 */
  private allowed = { download: false, print: false };
  /** 実際に動いているエンジン（降格すると 'css2d' になる）。 */
  private engineKind: Exclude<EngineKind, 'auto'> = 'css2d';
  private manifest: BookManifest | null = null;
  /** 表示中の閲覧パスワード入力面（EP-5）。dispose で中断する。 */
  private unlockForm: UnlockForm | null = null;
  /** ユーザーデータ（EP-1）。無効化時・非標準 source では null。 */
  private userData: UserData | null = null;
  /** このブックに付いている目印（種別ごと・ページ昇順）。 */
  private marks: Record<MarkType, UserItem[]> = { bookmark: [], sticky: [], shelf: [] };
  /** 会員セッション（個人データと公開コメントで共有し、問い合わせを 1 度にする）。 */
  private userSession: Session | null = null;
  private signedIn = false;
  /** 公開コメント・いいね（EP-1 公開データ）。無効化時・非標準 source では null。 */
  private social: Comments | null = null;
  private likes: LikeState = { count: 0, liked: false };
  /** 再開位置の保存を間引くタイマ。 */
  private progressTimer: number | null = null;

  private status: ViewerStatus = 'created';
  /** 確定した綴じ方向（options が auto ならマニフェストのコンテンツ属性に従う）。 */
  private direction: ReadingDirection = 'ltr';
  private page = 1;
  private zoom = 1;
  /** ズーム・パン後の URL 書き戻しを間引くタイマ。 */
  private hashTimer: number | null = null;
  /** 戻る/進むの着地処理中（この間の書き戻しは履歴を積まない）。 */
  private applyingHash = false;
  private reducedMotion = false;
  /** 遷移を直列化する（連続操作でアニメーションが交錯しないように）。 */
  private queue: Promise<unknown> = Promise.resolve();

  constructor(
    private readonly root: HTMLElement,
    args: ViewerOptionsInput = {},
  ) {
    this.options = resolveOptions(root, args);
    this.bus = new TypedEmitter<InternalEventMap>((error, type) => this.onHandlerError(error, type));

    const doc = root.ownerDocument;
    this.releaseStyles = acquireStyles(doc);
    this.releaseTheme = applyTheme(root, this.options.preset, this.options.theme);
    this.translate = createTranslator(this.options.i18n, doc);

    for (const [type, handler] of Object.entries(this.options.on)) {
      // キーごとの型対応は EventHandlers 型で保証済み。ここは走査のための単一化。
      const subscribe = this.bus.on.bind(this.bus) as (
        type: string,
        handler: (payload: unknown) => void,
      ) => void;
      subscribe(type, handler as (payload: unknown) => void);
    }

    this.shell = new Shell(
      root,
      this.registry,
      this,
      this.translate,
      this.options.ui,
      this.options.searchMinChars,
      this.options.share,
    );
    this.highlights = new HighlightLayer(this.shell.host, this.registry);
    // 組み込みの描き手。利用者が同じ type を登録し直せば差し替わる（EP-2 の公開口）。
    this.overlays.registerRenderer('link', linkRenderer);
    // 中身を持つ 3 種は payload の display で埋め込み／ポップアップを選べる（T012・D178）
    this.overlays.registerRenderer('video', byDisplay(videoRenderer, mediaPopupRenderer('video')));
    this.overlays.registerRenderer('audio', byDisplay(audioRenderer, mediaPopupRenderer('audio')));
    this.overlays.registerRenderer('form', byDisplay(formRenderer, formPopupRenderer));
    this.overlays.registerRenderer('media_note', mediaNoteRenderer);

    const view = doc.defaultView;
    this.sound = view
      ? new FlipSound(view, this.registry, {
          enabled: this.options.sound,
          file: this.options.soundFile,
          assetBase: this.options.assetBase,
        })
      : null;
    this.hash =
      this.options.hash && view
        ? new HashRouter(view, this.registry, root.id, (state) => this.applyHashState(state))
        : null;

    this.watchEnvironment();
    this.wireEngineEvents();

    // 収集（EP-6）はここで購読と資源登録だけ済ませ、宛先が決まる init() の途中で start() する。
    // registry への登録がコンストラクタの最後になることで、disposeAll() の逆順により
    // 「実行中の送信タイマを止める → 最後の 1 件を締めて送る → 他の層を壊す」の順になる。
    this.analytics = new Analytics({
      view,
      registry: this.registry,
      bus: this.bus,
      options: this.options.analytics,
      snapshot: () => ({ page: this.page, spread: this.engine?.spread ?? false }),
    });
  }

  /** 生成後の初期化。呼び出し側は ready イベント（または error）で完了を知る。 */
  async init(): Promise<void> {
    this.bus.emit('created', {});
    this.bus.emit('uiReady', {});
    this.status = 'loading';

    try {
      this.session = new ManifestSession(this.options.source);
      const manifest = await this.openManifest(this.session);
      if (this.stopped()) return;
      this.manifest = manifest;

      // 綴じ方向はコンテンツの属性（取り込み時に決まる）。auto なら追従する。
      this.direction =
        this.options.direction === 'auto'
          ? manifest.direction === 'rtl'
            ? 'rtl'
            : 'ltr'
          : this.options.direction;

      // 誌面本文の言語もコンテンツの属性。代替テキストの器へ置き、ホストページの言語に
      // 引きずられて誌面が読まれるのを防ぐ（WCAG 3.1.2 の土台・句や節は runs[].lang が持つ）。
      this.shell.setPageTextLang(manifest.lang ?? 'ja');

      // 配信モードはマニフェストで自動追従する（options.delivery による上書きも可）
      const choice = resolveDelivery(manifest, this.options.delivery);
      if (choice.reason) {
        console.warn(
          `[${GLOBAL_NAME}] 指定された配信モードの成果物が無いため ${choice.kind} で表示します（${choice.reason}）`,
        );
      }
      const source = createSource(choice.kind, {
        assetBase: this.options.assetBase,
        onError: (error) => this.fail(error),
        // 進捗を出せるのは総量が分かる PDF モードだけ。画像モードは不定形スピナーのまま。
        onProgress: (loaded, total) => {
          if (total > 0) this.shell.setProgress(loaded / total);
        },
        rangeChunkSize: this.options.rangeChunkSize,
      });
      // 閲覧範囲が絞られている本（立ち読み・EP-5）では、許可外ページへ要求を出さず
      // 「会員限定」の代替ページを描く装飾をかぶせる。
      this.provider = manifest.allowedPages
        ? new LockedPagesProvider(source, this.translate)
        : source;
      await this.provider.open(this.session);
      if (this.stopped()) return;

      // 原本の配布可否（FR-DL-01/02・FR-PRINT-01）。PDF 配信モードで、かつ PDF 自身が
      // 許しているときだけボタンを出す。画像配信モードには原本の概念が無いので出さない。
      this.delivery = choice.kind;
      const permissions = await source.permissions?.();
      if (this.stopped()) return;
      this.allowed = {
        download: this.options.ui.download && choice.kind === 'pdf' && (permissions?.download ?? false),
        print: this.options.ui.print && choice.kind === 'pdf' && (permissions?.print ?? false),
      };

      // サーバ生成サムネイルがあれば使う（無ければ provider の描画へ落ちる）
      this.thumbs = createThumbSource(this.session);
      // 誌面の代替テキスト（WCAG 1.1.1）。索引と同じテキスト層を読む。
      this.textSource = createTextSource(this.session);

      // EP-1（個人データ・公開データ）はどちらも API の基準 URL を manifest の source から導く。
      // 会員セッションは 1 つを共有する（/auth/session を 2 度叩かない）。
      const apiBase = apiBaseFrom(this.options.source);
      this.userSession = apiBase === null ? null : new Session(apiBase);
      // 再開位置（EP-1）はソースの用意と並行して取りに行く（起動を待たせない）
      this.userData = this.createUserData(apiBase);
      const savedPage = this.userData?.progress(manifest.slug) ?? null;
      this.social =
        apiBase !== null && this.options.ui.comments ? new Comments(apiBase, manifest.slug) : null;
      // 宛先が確定したので収集を開始する（ready より前 — 初回の page_view を取りこぼさない）
      this.analytics.start(manifest.slug, apiBase);

      this.mountEngine(this.engineContext(manifest));

      this.shell.build(manifest.pageCount, manifest.outline, manifest.textAvailable);

      const fromHash = this.hash?.start() ?? null;
      // 優先順は ハッシュ ＞ options.page ＞ 保存された再開位置
      const resumed =
        fromHash === null && this.options.page === 1 ? await this.resumePage(savedPage) : null;
      this.page = this.clamp(resumed ?? fromHash?.page ?? this.options.page);
      await this.engine?.goTo(this.page, { animate: false });
      if (this.stopped()) return;

      this.status = 'ready';
      this.shell.hidePreloader();
      // ズーム位置・ライトボックスの復元はページ確定後（setViewport は表示中ページにしか効かない）
      if (fromHash) this.restoreHashState(fromHash);
      this.writeHash();
      this.shell.sync();
      this.announcePage();
      this.updatePageText();
      if (resumed !== null) this.shell.announce(this.translate('resume.notice', { page: resumed }));
      this.saveProgress();
      // 公開データ（いいね件数）は目印の読み込みが終わってから取りに行く。目印は
      // ツールバーの押下状態を決める操作対象なので、先に確定させる。
      void this.loadMarks().then(() => this.loadSocial());
      void this.loadOverlays(manifest);
      this.bus.emit('ready', { pageCount: manifest.pageCount });
    } catch (error) {
      this.fail(error);
    }
  }

  /**
   * ユーザーデータ層（EP-1 個人データ）。`userData: false` か、source が標準形でなく
   * API の基準を導けないときは無効（null）にする。
   */
  private createUserData(base: string | null): UserData | null {
    if (!this.options.userData || base === null) return null;
    return new UserData({
      base,
      view: this.root.ownerDocument.defaultView,
      ...(this.userSession ? { session: this.userSession } : {}),
    });
  }

  /**
   * ログイン状態といいね件数を読む（EP-1 公開データ）。個人データと違い、コメントは
   * 未ログインでも読めるので、機能の有無だけで判断する。
   */
  private async loadSocial(): Promise<void> {
    this.signedIn = (this.userSession ? await this.userSession.user() : null) !== null;
    if (this.stopped()) return;
    const state = await this.social?.likeState();
    if (this.stopped()) return;
    if (state) this.likes = state;
    this.shell.sync();
  }

  /**
   * 誌面オーバーレイ（EP-2）を読み込んで層に載せる。
   * 定義が 1 件も無いブックにはマニフェストに URL が付かない（＝往復しない）。
   */
  private async loadOverlays(manifest: BookManifest): Promise<void> {
    const url = manifest.urls.overlays;
    if (!this.options.overlays || url === undefined) return;

    const layer = new OverlayLayer(
      this.shell.host,
      this.registry,
      this.overlays,
      {
        doc: this.root.ownerDocument,
        api: this,
        translate: this.translate,
        bookUrl: (slug, page) => this.bookUrl(slug, page),
        apiBase: apiBaseFrom(this.options.source),
        bookSlug: manifest.slug,
        unlocked: () => this.overlayUnlocked(),
      },
      // 計測（EP-6）は層のルートで拾ってバスへ載せる。層にバスを渡さないことで
      // 「emit は viewer.ts に集中させる」現状の不変条件を保つ。
      (item) => this.bus.emit('overlay:click', { id: item.id, type: item.type, page: item.page }),
    );
    this.overlayLayer = layer;

    layer.setOverlays(await fetchOverlays(url));
    if (this.stopped()) return;
    this.renderOverlays();
  }

  /**
   * ゲート通過（フォーム送信で解錠チケットが発行された）後の反映。
   * 許可範囲はマニフェスト取得時に確定しているため、部分再構築はせず読み込み直す
   * （割切り — M7b）。案内を `aria-live` で告げ、**読み込み直しは呼び出し側が
   * 利用者の操作に結び付ける**（時間で勝手に読み込み直すと、利用者に止める手段の無い
   * 制限時間になる — WCAG 2.2.1。読み上げの途中で画面が入れ替わる問題でもある）。
   */
  private overlayUnlocked(): () => void {
    const view = this.root.ownerDocument.defaultView;
    this.shell.announce(this.translate('overlay.form.unlocked'));
    return () => view?.location.reload();
  }

  /**
   * 別ブックの特定ページを指す絶対 URL（カタログ間リンク）。
   * 共有 URL と同じ正規ページ（/view/{slug}）とハッシュ語彙を使う — 遷移先でも
   * OGP プレビューが出て、ページ指定の意味が 1 つに保たれる。
   */
  private bookUrl(slug: string, page: number): string | null {
    const view = this.root.ownerDocument.defaultView;
    if (!view) return null;
    const canonical = canonicalViewUrl(
      this.options.source,
      view.location.href,
      this.options.share.viewBase,
      slug,
    );
    return canonical === null ? null : buildShareUrl(view, '', { page }, canonical);
  }

  /** 表示中ページのオーバーレイを現在の幾何に合わせて置き直す。 */
  private renderOverlays(): void {
    if (!this.overlayLayer?.active) return;
    this.overlayLayer.render((pageNo) => this.engine?.pageRect(pageNo) ?? null);
  }

  /** 保存された再開位置（1 ページ目や取得失敗は「復元しない」を意味する null）。 */
  private async resumePage(request: Promise<number | null> | null): Promise<number | null> {
    const page = await (request ?? Promise.resolve(null));
    return page !== null && page > 1 ? page : null;
  }

  /** 現在ページを再開位置として保存する（連続めくりは間引く）。 */
  private scheduleProgressSave(): void {
    const view = this.root.ownerDocument.defaultView;
    if (!this.userData || !view) return;
    if (this.progressTimer !== null) view.clearTimeout(this.progressTimer);
    this.progressTimer = view.setTimeout(() => {
      this.progressTimer = null;
      this.saveProgress();
    }, PROGRESS_SAVE_MS);
  }

  private saveProgress(): void {
    const slug = this.manifest?.slug;
    if (!this.userData || slug === undefined) return;
    void this.userData.saveProgress(slug, this.page);
  }

  /** このブックの目印を読み込む（一覧はブック横断で返るので現在のブックだけ残す）。 */
  private async loadMarks(): Promise<void> {
    const slug = this.manifest?.slug;
    if (!this.userData || slug === undefined) return;
    for (const type of MARK_TYPES) {
      const items = await this.userData.list(type);
      if (this.stopped()) return;
      this.marks[type] = items
        .filter((item) => item.book === slug)
        .sort((a, b) => (a.page ?? 0) - (b.page ?? 0));
    }
    this.shell.sync();
  }

  // -- 目印（EP-1）--------------------------------------------------------

  toggleMark(type: MarkType): void {
    const slug = this.manifest?.slug;
    if (!this.userData || slug === undefined) return;
    // 本棚はブック単位、しおり・ふせんは現在ページ
    const page = type === 'shelf' ? undefined : this.page;
    if (this.marks[type].some((item) => sameItem(item, slug, page))) {
      this.removeMark(type, page);
      return;
    }
    const item: UserItem = { book: slug, updatedAt: nowSec(), ...(page ? { page } : {}) };
    this.marks[type] = [...this.marks[type], item].sort((a, b) => (a.page ?? 0) - (b.page ?? 0));
    void this.userData.put(type, item);
    this.shell.sync();
  }

  listMarks(type: MarkType): readonly UserItem[] {
    return this.marks[type];
  }

  removeMark(type: MarkType, page?: number): void {
    const slug = this.manifest?.slug;
    if (!this.userData || slug === undefined) return;
    this.marks[type] = this.marks[type].filter((item) => !sameItem(item, slug, page));
    void this.userData.remove(type, slug, page);
    this.shell.sync();
  }

  // -- 公開コメント・いいね（EP-1 公開データ）--------------------------------

  toggleLike(): void {
    if (!this.social || !this.signedIn) return;
    const next = !this.likes.liked;
    // 楽観的に反映してから送る（応答を待って UI を止めない）
    this.likes = { count: Math.max(0, this.likes.count + (next ? 1 : -1)), liked: next };
    this.shell.sync();
    void this.social.setLike(next).then((state) => {
      if (this.stopped() || !state) return;
      this.likes = state;
      this.shell.sync();
    });
  }

  comments(): Comments | null {
    return this.social;
  }

  /** 現在ページ・現在ブックの目印状態（ツールバーの押下状態）。 */
  private markState(): Record<MarkType, boolean> {
    const slug = this.manifest?.slug;
    const state = { bookmark: false, sticky: false, shelf: false };
    if (slug === undefined) return state;
    for (const type of MARK_TYPES) {
      const page = type === 'shelf' ? undefined : this.page;
      state[type] = this.marks[type].some((item) => sameItem(item, slug, page));
    }
    return state;
  }

  /**
   * マニフェストを開く。パスワード保護（EP-5）で 403 が返ったときだけ入力面を出し、
   * 解錠できたら開き直す。それ以外のエラーはそのまま呼び出し元へ返す。
   */
  private async openManifest(session: ManifestSession): Promise<BookManifest> {
    try {
      return await session.open();
    } catch (error) {
      const url = isPasswordRequired(error) ? unlockUrlFrom(this.options.source) : null;
      if (url === null) throw error;

      // ここから先は「通信待ち」ではなく「人の入力待ち」なので起動表示は畳む。
      // 出したままだと入力面の手前に重なり、操作を奪ってしまう。
      this.shell.hidePreloader();

      // 描画面（shell.host）ではなくルート直下に出す。host は pointerdown で
      // ポインタキャプチャを取るため、その内側だとボタンの click が届かない。
      this.unlockForm = new UnlockForm(
        this.root.ownerDocument,
        this.root,
        this.registry,
        this.translate,
        url,
      );
      await this.unlockForm.wait();
      this.unlockForm = null;
      // 解錠後は読み込みの続きに戻るので、起動表示も戻す。
      this.shell.showPreloader();
      return session.open();
    }
  }

  // -- 公開 API ------------------------------------------------------------

  on<K extends PublicEventName>(type: K, handler: (payload: PublicEventMap[K]) => void): () => void {
    return this.bus.on(type, handler);
  }

  off<K extends PublicEventName>(type: K, handler?: (payload: PublicEventMap[K]) => void): void {
    this.bus.off(type, handler);
  }

  next(): Promise<void> {
    return this.navigate(this.page + (this.spreadStep() ? 2 : 1), false);
  }

  prev(): Promise<void> {
    return this.navigate(this.page - (this.spreadStep() && this.page > 2 ? 2 : 1), false);
  }

  first(): Promise<void> {
    return this.navigate(1, false);
  }

  last(): Promise<void> {
    return this.navigate(this.pageCount(), false);
  }

  /** 画面の左側にあるページへ（LTR なら前・RTL なら次）。 */
  flipLeft(): Promise<void> {
    return this.direction === 'rtl' ? this.next() : this.prev();
  }

  /** 画面の右側にあるページへ（LTR なら次・RTL なら前）。 */
  flipRight(): Promise<void> {
    return this.direction === 'rtl' ? this.prev() : this.next();
  }

  /**
   * ページ番号またはページラベルへ移動する。
   *
   * 目次・検索・サムネイル・ページ入力・オーバーレイのリンクといった「文脈が飛ぶ移動」は
   * すべてこの公開 API を通るため、履歴を 1 件積んで戻る/進むの着地点にする。連続めくり
   * （next/prev/first/last・スワイプ・矢印キー）は navigate(…, false) で履歴を汚さない。
   */
  goto(target: number | string): Promise<void> {
    return this.navigate(target, true);
  }

  private navigate(target: number | string, push: boolean): Promise<void> {
    return this.enqueue(async () => {
      const to = this.resolvePage(target);
      if (!this.engine || this.status !== 'ready' || to === this.page) return;

      const from = this.page;
      this.bus.emit('beforePageChange', { from, to });
      // めくり中は旧位置の矩形を残さない。3D の撓みに DOM は追随できないため、
      // オーバーレイも層ごと伏せて静止後に置き直す（EP-2 の共通規約）。
      this.highlights.hide();
      this.overlayLayer?.hide();
      await this.engine.goTo(to, { animate: true });
      this.page = to;
      this.writeHash(push);
      this.shell.sync();
      this.announcePage();
      this.updatePageText();
      this.renderHighlights();
      this.renderOverlays();
      this.scheduleProgressSave();
      this.bus.emit('afterPageChange', { page: to });
    });
  }

  zoomIn(): void {
    this.zoomTo(this.zoom * this.options.zoomStep);
  }

  zoomOut(): void {
    this.zoomTo(this.zoom / this.options.zoomStep);
  }

  resetZoom(): void {
    this.zoomTo(1);
  }

  zoomTo(level: number, focal?: { x: number; y: number }): void {
    const clamped = Math.min(Math.max(level, 1), this.options.zoomMax);
    if (clamped === this.zoom) return;
    this.zoom = clamped;
    this.bus.emit('viewer:zoom', { level: clamped, page: this.page });
    this.engine?.setZoom(clamped, focal);
    this.renderHighlights();
    this.renderOverlays();
    this.shell.sync();
    this.scheduleHashWrite();
  }

  panBy(dx: number, dy: number): void {
    this.engine?.panBy(dx, dy);
    this.renderHighlights();
    this.renderOverlays();
    // パンは shell.sync() を即座には呼ばない — ポインタ移動ごとにツールバー全体を
    // 書き直すと重い。URL 書き戻しと共有パネルの更新はまとめて間引く。
    this.scheduleHashWrite();
  }

  resize(): void {
    this.engine?.resize();
    this.renderHighlights();
    this.renderOverlays();
    // 単ページ ⇄ 見開きが切り替わると代替テキストの対象ページも変わる
    // （同じ並びなら updatePageText() 側で素通しする）。
    this.updatePageText();
    this.bus.emit('resize', {
      width: this.root.clientWidth,
      height: this.root.clientHeight,
    });
  }

  /**
   * 全画面の切り替え（FR-FS-01）。
   *
   * Fullscreen API が使えない環境（iOS Safari の要素全画面・`allow="fullscreen"` の無い
   * iframe など）では**ライトボックスへ落とす** — 要件の「API 不可の環境では CSS による
   * 擬似全画面にフォールバック」を、既にある画面全面表示で満たす（同じ見え方のものを
   * 2 つ持たない）。抜けるときの失敗は代替が無いので従来どおり error イベントへ回す。
   */
  toggleFullscreen(): void {
    const doc = this.root.ownerDocument;
    if (doc.fullscreenElement === this.root) {
      void doc
        .exitFullscreen()
        .then(() => this.shell.sync())
        .catch((error: unknown) => this.fail(error));
      return;
    }
    if (typeof this.root.requestFullscreen !== 'function' || !doc.fullscreenEnabled) {
      this.toggleLightbox();
      return;
    }
    void this.root
      .requestFullscreen()
      .then(() => this.shell.sync())
      .catch(() => this.toggleLightbox());
  }

  toggleLightbox(): void {
    this.shell.lightbox.toggle();
    this.resize();
    this.writeHash(true);
  }

  /** 操作ヘルプの開閉（`?` キーと同じ動作。`ui.help` が偽なら何も起きない）。 */
  toggleHelp(): void {
    this.shell.toggleHelp();
  }

  /** めくり音の有効・無効を切り替える（FR-SND-01）。 */
  toggleSound(): void {
    if (!this.sound) return;
    this.sound.setEnabled(!this.sound.enabled);
    this.shell.sync();
  }

  /**
   * 書内検索（FR-SEARCH-01）。サーバ索引へ問い合わせ、結果を searchDone で流し、
   * ヒットの誌面ハイライトを取り込む（表示中ページの分だけ描かれる）。
   * 新しい検索は前の要求を打ち切る。索引の無いブックでは空を返す。
   */
  search(query: string): void {
    const trimmed = query.trim();
    this.searchAbort?.abort();
    if (!this.session || trimmed === '') {
      this.searchAbort = null;
      this.highlights.clear();
      this.bus.emit('searchDone', { query: trimmed, hits: [] });
      return;
    }
    const controller = new AbortController();
    this.searchAbort = controller;
    void searchInBook(this.session, trimmed, controller.signal, this.page)
      .then((hits) => {
        if (controller.signal.aborted || this.stopped()) return;
        this.highlights.setHits(hits);
        this.renderHighlights();
        this.bus.emit('searchDone', { query: trimmed, hits });
      })
      .catch((error: unknown) => {
        if (!isAbortError(error)) this.fail(error);
      });
  }

  clearSearch(): void {
    this.searchAbort?.abort();
    this.searchAbort = null;
    this.highlights.clear();
    this.bus.emit('searchDone', { query: '', hits: [] });
  }

  /** 表示中ページのハイライトを現在の幾何に合わせて描き直す。 */
  private renderHighlights(): void {
    if (!this.highlights.active) return;
    this.highlights.render((pageNo) => this.engine?.pageRect(pageNo) ?? null);
  }

  /**
   * 原本 PDF をダウンロードする（FR-DL-01）。
   *
   * 署名 URL をそのまま開く（`download` 属性つきのリンクを 1 度だけ押す形）。PDF 配信モード
   * 以外、`ui.download` が偽、または PDF 自身が内容の抽出を禁じている場合は何も起きない。
   */
  download(): void {
    const url = this.originUrl();
    if (!url || !this.allowed.download) return;
    const link = this.root.ownerDocument.createElement('a');
    link.href = url;
    link.download = `${this.session?.current.slug ?? 'book'}.pdf`;
    link.rel = 'noopener';
    link.click();
  }

  /**
   * 原本 PDF を印刷用に開く（FR-PRINT-01）。
   *
   * 誌面のラスタではなく原本を新しいタブで開き、ブラウザの PDF ビューワの印刷に委ねる
   * （画像を並べ直すより忠実で、拡大中でも版面が崩れない）。`ui.print` が偽、PDF 配信モード
   * 以外、または PDF 自身が印刷を禁じている場合は何も起きない。
   */
  print(): void {
    const url = this.originUrl();
    if (!url || !this.allowed.print) return;
    this.root.ownerDocument.defaultView?.open(url, '_blank', 'noopener');
  }

  /** 原本 PDF の署名 URL（PDF 配信モードでなければ null）。 */
  private originUrl(): string | null {
    return this.delivery === 'pdf' ? (this.session?.current.urls.pdf ?? null) : null;
  }

  /** ツールバーへボタンを追加する（FR-UI-03）。 */
  addControl(def: ControlDef): void {
    this.shell.addControl(def);
  }

  getState(): ViewerState {
    return {
      page: this.page,
      label: this.pageLabel(this.page),
      pageCount: this.pageCount(),
      zoom: this.zoom,
      spread: this.engine?.spread ?? false,
      direction: this.direction,
      fullscreen: this.root.ownerDocument.fullscreenElement === this.root,
      lightbox: this.shell.lightbox.isOpen,
      engine: this.engineKind,
      sound: this.sound?.enabled ?? false,
      status: this.status,
      shareUrl: this.shareUrl(),
      marks: this.markState(),
      likes: this.likes.count,
      liked: this.likes.liked,
      signedIn: this.signedIn,
      canDownload: this.allowed.download,
      canPrint: this.allowed.print,
    };
  }

  outline(): readonly OutlineNode[] {
    return this.manifest?.outline ?? [];
  }

  pageLabel(pageNo: number): string {
    return this.manifest?.pages[pageNo - 1]?.label ?? String(pageNo);
  }

  /**
   * サムネイル用のラスタ。所有権は呼び出し側にあり、使い終えたら close する。
   * サーバ生成サムネイルがあればそれを使い（両配信モード）、無ければページ描画へ落ちる
   * （M4 以前に取り込んだブックとの互換）。
   */
  thumbnail(pageNo: number, width: number, signal: AbortSignal): Promise<PageRaster> {
    // 許可外ページ（EP-5）はサーバが 403 を返すので取りに行かず、provider の代替ページを使う。
    if (this.thumbs && isPageAllowed(this.manifest?.allowedPages, pageNo)) {
      return this.thumbs.get(pageNo, width, signal);
    }
    if (!this.provider) return Promise.reject(new ViewerError('E_STATE', 'ソースが未オープン'));
    return this.provider.renderPage({ pageNo, targetWidth: width, signal });
  }

  /** 生存中の資源数（エンジンが抱える分も合算）。dispose 後の残留ゼロを検証するための診断用。 */
  resourceCounts(): ResourceCounts {
    const counts = this.registry.activeCounts();
    const engine = this.engine?.resourceCounts?.();
    if (!engine) return counts;
    for (const kind of Object.keys(counts) as (keyof ResourceCounts)[]) {
      counts[kind] += engine[kind];
    }
    return counts;
  }

  /** 獲得した資源をすべて解放する。worker の終了まで待つため Promise を返す。 */
  async dispose(): Promise<void> {
    if (this.status === 'disposed') return;
    this.status = 'disposed';

    this.searchAbort?.abort();
    this.pageTextAbort?.abort();
    this.cancelHashWrite();
    if (this.progressTimer !== null) {
      this.root.ownerDocument.defaultView?.clearTimeout(this.progressTimer);
      this.progressTimer = null;
    }
    this.unlockForm?.close();
    this.unlockForm = null;
    this.registry.disposeAll();
    this.engine?.dispose();
    this.engine = null;
    await this.provider?.dispose();
    this.provider = null;
    this.session?.dispose();
    this.session = null;
    this.sound?.dispose();

    this.shell.dispose();
    this.releaseTheme();
    this.releaseStyles();
    this.bus.dispose();
  }

  // -- 内部 ----------------------------------------------------------------

  /**
   * 共有・URL 書き戻しのための現在状態（FR-LINK-03）。
   *
   * 拡大中は「画面中心が乗っているページ」を page とする — 見開きで対向ページを
   * 拡大して見ているとき、共有相手に開いてほしいのはそのページだから。等倍では
   * 従来どおり現在ページで、`#page=N` の意味は変わらない。
   */
  private hashState(): HashState {
    const viewport = this.zoom > 1 ? (this.engine?.getViewport() ?? null) : null;
    const state: HashState = { page: viewport?.page ?? this.page };
    if (viewport) {
      state.zoom = viewport.zoom;
      state.x = viewport.x;
      state.y = viewport.y;
    }
    if (this.shell.lightbox.isOpen) state.lightbox = true;
    return state;
  }

  /**
   * 現在のページ・ズーム位置を再現する絶対 URL。ハッシュナビが無効でも生成する。
   * 可能なら正規 OGP ページ（/view/{slug}）を指す（クローラ/SNS プレビュー・M5b-2）。
   * source が標準形でなければ現在ページへフォールバックする（後方互換）。
   */
  private shareUrl(): string {
    const view = this.root.ownerDocument.defaultView;
    if (!view) return '';
    const canonical = canonicalViewUrl(
      this.options.source,
      view.location.href,
      this.options.share.viewBase,
    );
    return buildShareUrl(view, this.hash?.prefix ?? '', this.hashState(), canonical);
  }

  /**
   * URL へ書き戻す。`push` は文脈が飛ぶ移動のときだけ true にする。
   * 戻る/進むの着地処理中（applyingHash）は、その移動自体が履歴の再生なので必ず replace。
   */
  private writeHash(push = false): void {
    this.cancelHashWrite();
    this.hash?.write(this.hashState(), { push: push && !this.applyingHash });
  }

  /**
   * ズーム・パン後の URL 書き戻しを間引く。
   * パンはポインタ移動ごとに走るため、間引かないと Safari の history スロットル
   * （30 秒あたり 100 回）に当たって以後の replaceState が黙って無視される。
   */
  private scheduleHashWrite(): void {
    const view = this.root.ownerDocument.defaultView;
    if (!view) return;
    this.cancelHashWrite();
    this.hashTimer = view.setTimeout(() => {
      this.hashTimer = null;
      this.hash?.write(this.hashState());
      // 共有パネルの URL 欄をパンに追従させる（新しい公開イベントは作らない）。
      this.shell.sync();
    }, HASH_WRITE_MS);
  }

  private cancelHashWrite(): void {
    if (this.hashTimer === null) return;
    this.root.ownerDocument.defaultView?.clearTimeout(this.hashTimer);
    this.hashTimer = null;
  }

  /** ハッシュ変更（戻る/進む・共有 URL の貼り直し）を反映する。 */
  private applyHashState(state: HashState): void {
    this.applyingHash = true;
    void this.navigate(state.page, false)
      .then(() => this.restoreHashState(state))
      .finally(() => {
        this.applyingHash = false;
      });
  }

  /** ページ確定後にズーム位置とライトボックスを復元する。 */
  private restoreHashState(state: HashState): void {
    if (state.lightbox === true && !this.shell.lightbox.isOpen) this.toggleLightbox();
    if (state.zoom === undefined) return;

    const zoom = Math.min(Math.max(state.zoom, 1), this.options.zoomMax);
    this.zoom = zoom;
    this.engine?.setViewport({ page: state.page, x: state.x ?? 0.5, y: state.y ?? 0.5, zoom });
    this.renderHighlights();
    this.renderOverlays();
    this.shell.sync();
  }

  /** 見開き表示かつ表紙より後なら、送りは 2 ページ単位。 */
  private spreadStep(): boolean {
    return (this.engine?.spread ?? false) && this.page > 1;
  }

  /** 内部（エンジン）イベントを公開イベントへ写す。 */
  private wireEngineEvents(): void {
    this.bus.on('engine:flipStart', (payload) => {
      this.bus.emit('beforeFlip', { direction: payload.direction });
    });
    this.bus.on('engine:flipEnd', (payload) => {
      this.bus.emit('afterFlip', { page: payload.page });
    });
    this.bus.on('engine:lost', (payload) => this.demote(payload.reason));
    // めくり音はめくりの開始に合わせる（ユーザー操作の文脈で鳴るため自動再生ポリシーを満たす）。
    this.bus.on('engine:flipStart', () => this.sound?.play());
  }

  /** エンジンを選んでマウントする。3D が使えなければ 2D へ降格する（FR-FLIP-03）。 */
  private mountEngine(context: EngineContext): void {
    const choice = selectEngine(this.options.engine, this.root.ownerDocument);
    try {
      choice.engine.mount(this.shell.host, context);
      this.engine = choice.engine;
      this.engineKind = choice.kind;
      if (choice.reason) {
        this.bus.emit('fallback', { from: 'webgl3d', to: 'css2d', reason: choice.reason });
      }
    } catch (error) {
      choice.engine.dispose();
      this.mountCss2d(context, 'mount-failed', error);
    }
  }

  /** 実行中に 3D が継続不能になったときの降格（GL コンテキスト消失など）。 */
  private demote(reason: EngineFailureReason): void {
    if (this.stopped() || this.engineKind !== 'webgl3d' || !this.manifest) return;

    this.engine?.dispose();
    this.engine = null;

    const engine = this.mountCss2d(this.engineContext(this.manifest), reason);
    // 降格前のページへ戻す（読んでいた場所を失わない）。
    void engine.goTo(this.page, { animate: false });
  }

  /** 2D エンジンを組み直し、公開 fallback イベントを発火する。 */
  private mountCss2d(
    context: EngineContext,
    reason: EngineFailureReason,
    cause?: unknown,
  ): RenderEngine {
    const engine = createFallbackEngine();
    engine.mount(this.shell.host, context);
    this.engine = engine;
    this.engineKind = 'css2d';
    this.bus.emit('fallback', {
      from: 'webgl3d',
      to: 'css2d',
      reason: cause instanceof Error ? `${reason}: ${cause.message}` : reason,
    });
    return engine;
  }

  /** 寸法変化・動きの抑制設定・全画面の変化を監視する。 */
  private watchEnvironment(): void {
    const view = this.root.ownerDocument.defaultView;
    if (!view) return;

    const observer = this.registry.observer(new ResizeObserver(() => this.resize()));
    observer.observe(this.root);

    const motion = view.matchMedia('(prefers-reduced-motion: reduce)');
    this.reducedMotion = motion.matches;
    this.registry.listen(motion, 'change', () => {
      this.reducedMotion = motion.matches;
      this.engine?.setContext({ reducedMotion: this.reducedMotion });
    });

    this.registry.listen(this.root.ownerDocument, 'fullscreenchange', () => {
      this.resize();
      this.shell.sync();
    });
  }

  private engineContext(manifest: BookManifest): EngineContext {
    const provider = this.provider;
    if (!provider) throw new ViewerError('E_STATE', 'ソースが未オープン');
    return {
      pages: manifest.pages,
      direction: this.direction,
      mode: this.options.mode,
      hardcover: this.options.hardcover,
      reducedMotion: this.reducedMotion,
      raster: (request) => provider.renderPage(request),
      maxRasterWidth: (pageNo) => provider.maxRasterWidth(pageNo),
      maxEffectiveWidth: (pageNo) => provider.maxEffectiveWidth(pageNo),
      bus: this.bus,
    };
  }

  private pageCount(): number {
    return this.manifest?.pageCount ?? 0;
  }

  private clamp(pageNo: number): number {
    return Math.min(Math.max(Math.trunc(pageNo), 1), Math.max(this.pageCount(), 1));
  }

  /** ページ番号またはラベルを、実在するページ番号へ解決する。 */
  private resolvePage(target: number | string): number {
    if (typeof target === 'number') return this.clamp(target);
    const byLabel = this.manifest?.pages.find((page) => page.label === target);
    return this.clamp(byLabel?.no ?? (Number.parseInt(target, 10) || 1));
  }

  /**
   * ページ遷移の告知。遮断ページ（EP-5）では理由も続けて伝える —
   * canvas に描いた文言は支援技術に届かないため、ここが唯一の通知経路になる。
   */
  private announcePage(): void {
    const heading = this.pageHeading(this.page);
    const notice = this.lockedNoticeFor(this.page);
    this.shell.announce(
      notice === null ? heading : `${heading} ${this.translate('locked.notice', notice)}`,
    );
  }

  /** ページが塞がれていれば、その理由の案内文（塞がれていなければ null）。 */
  private lockedNoticeFor(pageNo: number): { title: string; hint: string } | null {
    if (isPageAllowed(this.manifest?.allowedPages, pageNo)) return null;
    return lockedNotice(this.translate, this.manifest?.lockedPages, pageNo);
  }

  /** 「N ページ / 全 M」— 読み上げの告知と代替テキストの見出しで同じ文言を使う。 */
  private pageHeading(pageNo: number): string {
    return this.translate('page.current', {
      page: this.pageLabel(pageNo),
      total: this.pageCount(),
    });
  }

  /** いま誌面に出ているページ（見開きなら 2 枚）。ページ番号の昇順＝読み順。 */
  private visiblePages(): number[] {
    if (!this.engine?.spread) return [this.page];
    const spread = spreadAt(spreadOf(this.page), this.pageCount(), this.direction);
    return pagesOf(spread).sort((a, b) => a - b);
  }

  /**
   * 誌面の代替テキストを更新する（WCAG 1.1.1）。
   *
   * 見出し行を先に出し、本文は届いてから足す（取得を待って読み上げを止めない）。
   * テキスト層の無い本・許可外ページ（EP-5）では本文が null のままになり、
   * 「N ページ / 全 M」だけの縮退表示になる。
   */
  private updatePageText(): void {
    // 読み込み中に来る resize（ResizeObserver は初回に必ず 1 度発火する）では何もしない。
    // マニフェスト未取得のまま「0 ページ中 1 ページ目」を書き、以後キーが一致して
    // 本来の更新が素通りしてしまう。
    if (this.status !== 'ready') return;
    const pages = this.visiblePages();
    const key = pages.join(',');
    if (key === this.pageTextKey) return;
    this.pageTextKey = key;

    this.pageTextAbort?.abort();
    const controller = new AbortController();
    this.pageTextAbort = controller;

    const entry = (pageNo: number, body: readonly TextSegment[] | null) => {
      const notice = this.lockedNoticeFor(pageNo);
      // 遮断ページは本文の代わりに理由の案内を出す（canvas の文言と同じもの）。
      const lines = notice ? [notice.title, notice.hint] : body && body.length > 0 ? [body] : [];
      return { heading: this.pageHeading(pageNo), lines };
    };
    this.shell.setPageText(pages.map((no) => entry(no, null)));

    const source = this.textSource;
    if (!source) return;
    void Promise.all(pages.map((no) => source.get(no, controller.signal))).then((bodies) => {
      if (controller.signal.aborted || this.stopped()) return;
      this.shell.setPageText(pages.map((no, index) => entry(no, bodies[index] ?? null)));
    });
  }

  /** 遷移を直列化する（前の遷移の成否によらず次を実行する）。 */
  private enqueue<T>(task: () => Promise<T>): Promise<T> {
    const next = this.queue.then(task, task);
    this.queue = next.catch(() => undefined);
    return next;
  }

  private stopped(): boolean {
    return this.status === 'disposed';
  }

  private onHandlerError(error: unknown, type: keyof InternalEventMap): void {
    // error ハンドラ内の例外を再び error として流すと無限に回るため、そこだけは打ち切る。
    if (type === 'error') return;
    this.fail(error);
  }

  private fail(error: unknown): void {
    if (isAbortError(error) || this.stopped()) return;
    this.status = 'error';
    // 失敗しても起動表示は残さない（回り続けるスピナーで待たせない）。
    this.shell.hidePreloader();
    const known = error instanceof ViewerError;
    this.bus.emit('error', {
      code: known ? error.code : 'E_UNEXPECTED',
      message: error instanceof Error ? error.message : String(error),
      cause: error,
    });
  }
}
